import { createSelector } from '@reduxjs/toolkit';

import { AssetContent_Study_CBCT_CBCT_Metadata } from '@/shared/api/protocol-ts/model/dto_asset_study_pb';
import {
  ReportStatus,
  ReportType,
} from '@/shared/api/protocol-ts/model/dto_report_pb';
import { Report_CBCT_IOS_Superimposition_Orientation } from '@/shared/api/protocol-ts/model/dto_report_type_cbct_ios_superimposition_pb';
import { Study, StudyType } from '@/shared/api/protocol-ts/model/dto_study_pb';
import { StudyMetaInfo } from '@/shared/config/types';
import { getCBCTMetaData } from '@/shared/lib';

import { assetsModel } from '@/entities/assets';
import { reportsModel } from '@/entities/reports';
import { isStudyUploadFailed, studyModel } from '@/entities/study';

import { ReportMetaInfo } from '../config/types';

export const selectReportInfo = (
  sourceStudyIDs: string[],
  reportType: ReportType,
  orientation?: Report_CBCT_IOS_Superimposition_Orientation,
) =>
  createSelector(
    reportsModel.selectors.selectActiveReportsByStudyIDsAndType(
      sourceStudyIDs,
      reportType,
    ),
    studyModel.selectors.selectEntities,
    assetsModel.selectors.selectAll,
    (allRelevantReports, studyEntities, assets) => {
      const relevantReports =
        orientation === undefined
          ? allRelevantReports
          : allRelevantReports.filter(
              (report) =>
                report.Specific.case === 'DataCBCTIOSSuperimposition' &&
                report.Specific.value.Orientation === orientation,
            );

      const studies: Study[] = [];

      sourceStudyIDs.forEach((studyID) => {
        if (studyID in studyEntities) {
          studies.push(studyEntities[studyID] as Study);
        }
      });

      const studiesMetaInfo = studies.map((study): StudyMetaInfo => {
        const CBCTMetaData =
          study.Type === StudyType.StudyType_CBCT
            ? getCBCTMetaData(study.ID)(assets)
            : ({} as AssetContent_Study_CBCT_CBCT_Metadata);

        return {
          ID: study.ID,
          studyType: study.Type,
          scanUID: CBCTMetaData?.ScanUID,
          manufacturer: CBCTMetaData?.Manufacturer,
          model: CBCTMetaData?.ManufacturerModelName,
          exposure: CBCTMetaData?.Exposure,
          fov: CBCTMetaData?.FOV,
          voxel: CBCTMetaData?.VoxelSize?.join(','),
          uploadDate: study.Created?.At?.toDate(),
          studyTime: study.Time?.toDate(),
        };
      });

      const reportsInfo = relevantReports.map(
        (report): ReportMetaInfo => ({
          reportID: report.ID,
          reportType: report.Type,
          patientID: report.PatientID,
          date: report.Created?.At?.toDate() as Date,
          status: report.Status as ReportStatus,
        }),
      );

      return {
        reportsInfo,
        studiesMetaInfo,
        hasFailedStudyUpload: studies.some(isStudyUploadFailed),
      };
    },
  );
